import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Divider,
  Typography,
  Grid,
  FormControl,
  MenuItem,
  InputLabel
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { Formik, Form, Field, useFormikContext } from "formik";
import { TextField, Select } from "formik-mui";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import { UploadButton } from "../../components/Buttons";
import { DatePickerField, RadioGroup } from "../../components/FormikFields";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import UploadField from "../../components/FileUpload/FileUploadField";
import usePermissions from "../../redux/selectors/usePermissions";
import { Tooltip } from "../../components/Tooltip";

const minuteDurationSet = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
const descriptionMaxCharacters = 300;
const certificateAcceptType = ".pdf"; //.jpg, .png, .doc, .docx formats will be added in the future

const getInitialValues = currentTrainingRecord => ({
  name: currentTrainingRecord ? currentTrainingRecord.trainingObject.name : "",
  score: currentTrainingRecord
    ? currentTrainingRecord.trainingObject.score
    : "no",
  postScore: currentTrainingRecord ? currentTrainingRecord.postScore : 0,
  durationHours: currentTrainingRecord
    ? currentTrainingRecord.trainingObject.durationHours
    : 0,
  durationMinutes: currentTrainingRecord
    ? currentTrainingRecord.trainingObject.durationMinutes
    : 0,
  completionDate: currentTrainingRecord
    ? currentTrainingRecord.completedAt
    : new Date(),
  description:
    currentTrainingRecord && currentTrainingRecord.trainingObject.description
      ? currentTrainingRecord.trainingObject.description
      : ""
});

const OtherTrainingRecordModalForm = ({
  onClose,
  isSubmitting,
  open,
  isExistingRecord,
  onCertificateChange,
  certificate,
  allowEditRecord
}) => {
  const { t } = useTranslation();
  const { values, resetForm, submitForm } = useFormikContext();

  useEffect(() => {
    resetForm();
  }, [open]);

  const handleCertificateUpload = event => {
    if (event.target && event.target.files) {
      onCertificateChange(event.target.files[0]);
    }
  };

  const handleDeleteCertificate = () => {
    onCertificateChange(null);
  };

  return (
    <Modal
      scrollable
      maxWidth="md"
      fullWidth
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {isExistingRecord
            ? t("otherTrainingRecords.editTitle")
            : t("otherTrainingRecords.addTitle")}
        </Typography>
      }
      dialogContent={
        <Form>
          <Box>
            <Field
              variant="filled"
              fullWidth
              label={t("table.columnHeader.Name")}
              component={TextField}
              name="name"
              inputProps={{
                "data-qa": "add-other-training-record-modal-name-input"
              }}
            />
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems="center"
            >
              <Box display="flex" flex={1} maxWidth={290} my={2.5}>
                <Box mr={3} flex={1}>
                  <RadioGroup
                    radioButtons={[
                      { label: t("yes"), value: "yes" },
                      {
                        label: t("no"),
                        value: "no"
                      }
                    ]}
                    name="score"
                    label={t("table.columnHeader.score")}
                    disabled={isSubmitting}
                  />
                </Box>
              </Box>
              <Box>
                {values.score === "yes" && (
                  <Field
                    variant="filled"
                    fullWidth
                    label={t("otherTrainingRecords.postScore")}
                    component={TextField}
                    name="postScore"
                    disabled={isSubmitting}
                  />
                )}
              </Box>
            </Box>
            <Divider />
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              {t("otherTrainingRecords.trainingHours")}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Field
                  variant="filled"
                  fullWidth
                  label={t("otherTrainingRecords.hoursField")}
                  component={TextField}
                  name="durationHours"
                  type="number"
                  inputProps={{ min: 0 }}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel shrink={true} htmlFor="durationMinutes">
                    {t("otherTrainingRecords.minutesField")}
                  </InputLabel>
                  <Field
                    type="text"
                    name="durationMinutes"
                    component={Select}
                    inputProps={{
                      name: "durationMinutes",
                      id: "durationMinutes"
                    }}
                    variant="filled"
                    fullWidth
                    renderValue={value => `${value} ${t("minutesAbbreviated")}`}
                    disabled={isSubmitting}
                  >
                    {minuteDurationSet.map((item, ind) => (
                      <MenuItem value={item} key={ind}>
                        {item}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field
                  name="completionDate"
                  component={DatePickerField}
                  label={t("otherTrainingRecords.completionDate")}
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>
            <Divider sx={{ my: 3 }} />
            <Box display="flex" flexDirection="column">
              <Box display="flex" flexDirection="column">
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t("otherTrainingRecords.certificate")}
                </Typography>
                {!!certificate && allowEditRecord && (
                  <Box mb={2}>
                    <Alert severity="info">
                      {t("otherTrainingRecords.uploadNewFileWarning")}
                    </Alert>
                  </Box>
                )}
                <UploadField
                  value={certificate ? certificate.name : null}
                  onUpload={handleCertificateUpload}
                  helperText=".pdf file format go here"
                  placeholder={t("textFieldUploadButton.buttonLabel")}
                  acceptType={certificateAcceptType}
                  disabled={isSubmitting || !allowEditRecord}
                  endAdornment={
                    certificate && allowEditRecord ? (
                      <ButtonGroup alignItems="center">
                        <Button
                          variant="contained"
                          color="primary"
                          href={certificate.url}
                          target="_blank"
                          download={certificate.name}
                          disabled={isSubmitting}
                        >
                          {t("otherTrainingRecords.viewFileBtn")}
                        </Button>
                        <Tooltip
                          title={t("otherTrainingRecords.uploadNewFileTooltip")}
                        >
                          <div>
                            <UploadButton
                              acceptType={certificateAcceptType}
                              buttonText={t(
                                "otherTrainingRecords.updateFileBtn"
                              )}
                              buttonVariant="contained"
                              buttonId="certificate-update"
                              onUpload={handleCertificateUpload}
                              disabled={isSubmitting}
                            />
                          </div>
                        </Tooltip>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleDeleteCertificate()}
                          disabled={isSubmitting}
                        >
                          {t("otherTrainingRecords.deleteFileBtn")}
                        </Button>
                      </ButtonGroup>
                    ) : null
                  }
                />
              </Box>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Field
              variant="filled"
              fullWidth
              label={t("manageCourses.courseSettingsDescriptionField")}
              component={TextField}
              name="description"
              multiline
              helperText={`${
                values.description
                  ? descriptionMaxCharacters - values.description.length
                  : descriptionMaxCharacters
              }/${descriptionMaxCharacters} ${t("charactersRemaining")}`}
              rows={3}
              inputProps={{
                "data-qa": "add-other-training-record-modal-description-input"
              }}
              disabled={isSubmitting}
            />
          </Box>
        </Form>
      }
      dialogActions={
        <>
          <Button
            color="primary"
            disabled={isSubmitting}
            onClick={() => onClose()}
          >
            {t("cancelBtn")}
          </Button>
          {!allowEditRecord ? (
            <Tooltip title={t("otherTrainingRecords.disableSaveBtnTooltip")}>
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={submitForm}
                  disabled={!allowEditRecord}
                >
                  {t("saveBtn")}
                </Button>
              </div>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={submitForm}
              disabled={isSubmitting}
            >
              {t("saveBtn")}
            </Button>
          )}
        </>
      }
      open={open}
      onClose={() => onClose()}
      showCloseIcon
    />
  );
};
const getNewRecordSchema = t => {
  const maxNameLength = 150;

  return Yup.object().shape({
    name: Yup.string()
      .required(t("validationMessages.nameRequired"))
      .max(
        maxNameLength,
        t("validationMessages.cannotExceedLength", {
          type: "Name",
          maxLength: maxNameLength
        })
      ),
    durationHours: Yup.number()
      .typeError(t("validationMessages.validNumber"))
      .min(0, t("validationMessages.validNumber"))
      .test(
        "durationRequired",
        t("validationMessages.requiredDuration"),
        function(val) {
          return this.parent.durationMinutes === 0 ? !!val : true;
        }
      ),
    description: Yup.string().max(
      300,
      t("validationMessages.cannotExceedLength", {
        type: t("manageCourses.addCourseDescriptionField"),
        maxLength: 300
      })
    ),
    postScore: Yup.mixed().test(
      "passingScoreRequired",
      t("validationMessages.validNumber"),
      function() {
        return (
          this.parent.score === "no" ||
          (this.parent.score === "yes" &&
            Number.isInteger(+this.parent.postScore) &&
            this.parent.postScore >= 0 &&
            this.parent.postScore <= 100)
        );
      }
    )
  });
};

function OtherTrainingRecordModal(props) {
  const {
    open,
    onClose,
    submitTrainingRecord,
    currentTrainingRecord,
    isSubmitting
  } = props;
  const [currentCertificate, setCurrentCertificate] = useState(null);
  const [certificateToUpload, setCertificateToUpload] = useState(null);
  const { t } = useTranslation();
  const [initialValues, setInitialValues] = useState(
    getInitialValues(currentTrainingRecord)
  );
  const { isAdmin } = usePermissions();
  const allowEditRecord =
    !currentTrainingRecord ||
    (currentTrainingRecord.createdByAdmin && isAdmin) ||
    !currentTrainingRecord.createdByAdmin;

  useEffect(() => {
    const defaultinitialValues = getInitialValues(currentTrainingRecord);
    setInitialValues(defaultinitialValues);
    if (currentTrainingRecord && currentTrainingRecord.certificate) {
      setCurrentCertificate(currentTrainingRecord.certificate);
    }
  }, [currentTrainingRecord]);

  useEffect(() => {
    if (!open) {
      setCurrentCertificate(null);
      setCertificateToUpload(null);
    }
  }, [open]);

  const handleSubmitTrainingRecord = values => {
    const newValues = { ...values };
    if (currentTrainingRecord && !currentCertificate && !certificateToUpload) {
      // The certificate was deleted so we'll send certificate as null
      newValues.certificate = null;
    } else if (!!certificateToUpload) {
      // A new certificate was added
      newValues.certificate = certificateToUpload;
    }
    submitTrainingRecord(newValues);
  };

  const handleCertificateChange = newCertificate => {
    setCertificateToUpload(newCertificate);
    setCurrentCertificate(
      newCertificate
        ? {
            url: URL.createObjectURL(newCertificate),
            name: newCertificate.name
          }
        : null
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getNewRecordSchema(t)}
      enableReinitialize
      onSubmit={values => {
        handleSubmitTrainingRecord(values);
      }}
    >
      {() => (
        <OtherTrainingRecordModalForm
          open={open}
          onClose={onClose}
          isExistingRecord={!!currentTrainingRecord}
          certificate={currentCertificate}
          onCertificateChange={handleCertificateChange}
          isSubmitting={isSubmitting}
          allowEditRecord={allowEditRecord}
        />
      )}
    </Formik>
  );
}

OtherTrainingRecordModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  submitTrainingRecord: PropTypes.func,
  currentTrainingRecord: PropTypes.any,
  isSubmitting: PropTypes.bool
};

export default OtherTrainingRecordModal;
